import { useMemo, useState } from "react";
import DashboardFilters from "@/components/dashboard/DashboardFilters";
import KPIGrid from "@/components/dashboard/KPIGrid";
import ForecastSection from "@/components/dashboard/ForecastSection";
import TaxBreakdownChart from "@/components/dashboard/charts/TaxBreakdownChart";
import useDashboardData from "@/hooks/useDashboardData";
import { Card, CardContent } from "@/components/ui/card";
import DashboardRangeNotice from "@/components/dashboard/DashboardRangeNotice";

export default function DashboardTaxes() {
  const [forecastScenario, setForecastScenario] = useState("neutral");
  const {
    forecast,
    forecastTax,
    forecastLoading,
    forecastError,
    revenueSeries,
    expenseSeries,
    rangeMode,
    forecastStartLabel,
    forecastMonthsAhead,
    rangeStart,
    rangeEnd,
  } = useDashboardData({
    includeStatus: false,
    includeProjects: false,
    includeCustomers: false,
    forecastScenario,
  });

  const kpiItems = useMemo(() => {
    if (!forecastTax) return [];
    return [
      {
        key: "tax-total",
        title: "Gesamtsteuer",
        value: forecastTax.total || 0,
        testId: "kpi-tax-total",
      },
      {
        key: "tax-reserve",
        title: "Monatliche Rücklage (ESt + SV)",
        value: forecastTax.monthly_reserve || 0,
        testId: "kpi-tax-reserve",
      },
      {
        key: "tax-vat",
        title: "Umsatzsteuer",
        value: forecastTax.vat || 0,
        testId: "kpi-tax-vat",
      },
      {
        key: "tax-social",
        title: "Sozialabgaben",
        value: forecastTax.social_security || 0,
        testId: "kpi-tax-social",
      },
    ];
  }, [forecastTax]);

  const showForecast = rangeMode === "mixed";

  return (
    <div className="space-y-6" data-testid="dashboard-taxes-view">
      <DashboardFilters
        title="Steuer-Filter"
        subtitle="Plane Rücklagen nach Zeitraum"
        showProjects={false}
        showCustomers={false}
        showStatus={false}
      />

      <DashboardRangeNotice mode={rangeMode} forecastStartLabel={forecastStartLabel} />

      <section data-testid="dashboard-taxes-kpis">
        {!showForecast && (
          <Card>
            <CardContent className="p-6 text-sm text-slate-600" data-testid="dashboard-no-data">
              Die Steuerprognose wird nur angezeigt, wenn der Zeitraum in die Zukunft reicht.
            </CardContent>
          </Card>
        )}
        {forecastLoading && <KPIGrid isLoading />}
        {!forecastLoading && forecastError && (
          <Card>
            <CardContent className="p-6 text-sm text-red-600" data-testid="kpi-error">
              Steuerdaten konnten nicht geladen werden.
            </CardContent>
          </Card>
        )}
        {showForecast && !forecastLoading && !forecastError && kpiItems.length > 0 && (
          <KPIGrid items={kpiItems} />
        )}
      </section>

      {showForecast && (
        <section data-testid="dashboard-taxes-charts">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TaxBreakdownChart taxEstimate={forecastTax} isLoading={forecastLoading} error={forecastError} />
            <ForecastSection
              historicalRevenue={revenueSeries}
              historicalExpenses={expenseSeries}
              forecastData={forecast}
              taxEstimate={forecastTax}
              isLoading={forecastLoading}
              error={forecastError}
              scenario={forecastScenario}
              onScenarioChange={setForecastScenario}
              forecastMonths={forecastMonthsAhead}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
            />
          </div>
        </section>
      )}
    </div>
  );
}